<template>
  <component v-if="childElementType" :is="childElementType">
    <slot v-if="loading && !error" name="loading" />
    <slot v-if="error" name="error" v-bind:error="error" />
    <slot v-if="data" v-bind:data="data" />
  </component>
  <RenderSlot v-else :render-slot="renderSlot" :slot-props="{ data, error }" />
</template>

<script>
/**
 * DataGetter
 * -----------------------
 * Declarative, reactive wrapper for axios to fetch data only when props change and avoid watcher logic hell of
 * imperative fetching. Passes to children data through v-slot. New version uses events to handle side effects or upper-scope
 * data usage/transformation.
 */
import RenderSlot from "./RenderSlot.vue";
import axios from "axios";
import hash from "object-hash";
// NB THE DATA CACHE IS SHARED ACROSS ALL DATA GETTERS!
const dataCache = new Map(); // Vue < 3 doesn't support reactive Maps so keep it outside the component
export default {
  components: { RenderSlot },
  props: {
    /**
     * URL to query
     */
    url: {
      type: String,
      required: true,
    },
    /**
     * Query paramaters to use. NB arrays are serialized with comma seperator.
     */
    params: {
      type: Object,
      default: () => {},
    },
    /**
     * Formatter to use on the data fetched.
     */
    formatter: {
      type: Function,
      default: null,
    },
    /**
     * Specify the type of child component
     */
    childElementType: {
      type: String,
      default: "div",
    },
    /**
     * Enable/disable data caching
     */
    cached: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: true,
      error: false,
      data: null,
      useParams: {},
      paramHash: null,
    };
  },
  computed: {
    /**
     * If childElementType is null, select the slot to render
     */
    renderSlot() {
      const { loading, error } = this;
      let slot = "default";
      if (loading) slot = "loading";
      if (error) slot = "error";
      return this.$scopedSlots[slot] || this.$scopedSlots.default;
    },
  },
  methods: {
    formatParams(parameters) {
      const params = { ...parameters };
      Object.keys(params).forEach((param) => {
        if (Array.isArray(params[param])) {
          if (params[param].some((p) => !!p && isNaN(p) && p.includes(","))) {
            params[param] = params[param].map((p) => `"${p}"`);
          }
          params[param] = params[param].join(",");
        } else if (
          params[param] &&
          typeof params[param] === "string" &&
          params[param].includes(",")
        ) {
          params[param] = `"${params[param]}"`;
        }
      });
      return params;
    },
    getData() {
      const { url, cached, useParams } = this;
      // unique hash of params for use with caching
      const paramHash = hash(
        {
          url: this.url,
          ...this.params,
          salt: this.formatter ? Date.now() : 0,
        },
        "md5"
      );
      this.paramHash = paramHash;
      // check if the data already exists in the cache
      if (cached && dataCache.has(paramHash)) {
        setTimeout(() => (this.data = dataCache.get(paramHash)), 10);
        return;
      }
      axios
        .get(url, { params: useParams })
        .then((res) => {
          // Ignore the result if the url/params have since changed
          if (this.paramHash === paramHash) {
            const newData = this.formatter
              ? this.formatter(res.data, this.params)
              : res.data;
            this.data = newData;
            dataCache.set(paramHash, newData);
          }
        })
        .catch((err) => {
          this.error = err;
          this.loading = false;
          this.$emit("error", err);
        });
    },
    /**
     * Handles rendering of named slots with the option for no wrapping element
     * (childElementType === null)
     * Returns null if no such named slot
     */
    tryRenderSlot(h, slotName, slotProps = {}) {
      const slot = this.$scopedSlots[slotName];
      const { childElementType } = this;
      if (!slot) return null;
      const slotVNode = slot(slotProps);
      // Handle rendering without wrapping component
      if (childElementType === null) {
        return slotVNode;
      }
      // otherwise render slot inside childElement
      return h(childElementType, { key: slotName }, slotVNode);
    },
  },
  watch: {
    data(newData) {
      if (newData !== null) {
        this.loading = false;
        this.error = false;
        this.$emit("fetched", newData);
      }
    },
    params: {
      deep: true,
      immediate: true,
      handler() {
        const formattedParams = JSON.stringify(this.formatParams(this.params));
        if (
          !this.params ||
          !Object.keys(this.params).length ||
          formattedParams !== JSON.stringify(this.useParams)
        ) {
          this.useParams = this.formatParams(this.params);
          this.error = false;
          this.loading = true;
          this.data = null;
          this.$emit("loading");
          this.getData();
        }
      },
    },
    url() {
      this.error = false;
      this.loading = true;
      this.data = null;
      this.$emit("loading");
      this.getData();
    },
  },
};
</script>
